import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCog, FaHeart, FaMagic, FaPlus, FaTimes, FaCheck, FaCrown } from 'react-icons/fa';
import { API } from '../App';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { grantWelcomeAccess } from '../utils/welcomeAccess';
import { markGuestOnboardingCompleted } from '../utils/guestOnboarding';
import { trackEvent, ANALYTICS_EVENTS } from '../utils/analytics';
import { SUBSCRIPTIONS, SUBSCRIPTION_ORDER, hasPremiumAccess } from '../config/subscriptions';

/**
 * First-time onboarding wizard shown ONCE per user (controlled by
 * `onboarding_completed` flag on the user record).
 *
 * Steps:
 *   1. Pick machine
 *   2. Pick interest categories (multi-select)
 *   3. Welcome card with two CTAs (Smart Match / Create recipe)
 *
 * User can skip at any time. Existing users were migrated to
 * `onboarding_completed: true` at backend startup so this never re-appears
 * for them.
 */

const INTEREST_OPTIONS = [
  { key: 'slushice', label: 'Slushice', emoji: '🧊' },
  { key: 'frappe', label: 'Kaffe / Frappé', emoji: '☕' },
  { key: 'milkshake', label: 'Mælk / Milkshake', emoji: '🥤' },
  { key: 'frozen_cocktails', label: 'Cocktail / Frozen Cocktails', emoji: '🍹' },
];

const WelcomeOnboarding = ({ open: openProp, onClose }) => {
  const { t } = useTranslation();
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();

  // Treat anonymous visitors AND signed-up free-tier users (role==='guest')
  // identically — both must not be able to manually replay onboarding once
  // they've finished it the first time. Pro / editor / admin are the only
  // roles that keep the Guide button.
  const isGuestUser = !hasPremiumAccess(user);

  const [step, setStep] = useState(1);

  // Listen for a global "open me again" event so any nav icon, settings
  // page, or future help-center entry can re-launch this guide without
  // having to thread setShowOnboarding props down the component tree.
  // The original prop still wins on first-time onboarding (set from
  // HomePage when user.onboarding_completed === false).
  const [manualOpen, setManualOpen] = useState(false);
  const open = openProp || manualOpen;
  React.useEffect(() => {
    const reopen = () => {
      setStep(1);
      setManualOpen(true);
    };
    window.addEventListener('slushbook:open-onboarding', reopen);
    return () => window.removeEventListener('slushbook:open-onboarding', reopen);
  }, []);

  const closeOnboarding = () => {
    // For any "guest" (anonymous OR role==='guest'), remember that they've
    // seen the guide so the topbar Guide button can hide itself. This
    // prevents guests from replaying onboarding and accidentally bypassing
    // the 15-free-recipes limit or other guest-tier restrictions.
    if (isGuestUser) {
      markGuestOnboardingCompleted();
    }
    setManualOpen(false);
    setStep(1); // reset so next manual reopen starts from the beginning
    onClose?.();
  };

  const [machines, setMachines] = useState([]);
  const [machinesLoading, setMachinesLoading] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState('');
  const [customMachineName, setCustomMachineName] = useState('');
  const [machineVolume, setMachineVolume] = useState(''); // always stored as liters
  // Lets the user explicitly pick the unit they're typing in — dramatically
  // reduces "I typed 1.89 thinking it was 1890 ml" misreads. Internally we
  // ALWAYS persist liters; the toggle just controls how the typed number
  // is interpreted. Default = ml because most consumer machines (Ninja,
  // Camry, Emerio) are spec'd in ml on the box (700 ml, 1300 ml, …).
  const [volumeUnit, setVolumeUnit] = useState('ml'); // 'L' | 'ml'
  const [interests, setInterests] = useState([]);
  const [saving, setSaving] = useState(false);

  // True for both the generic "Anden maskine" and any brand-specific
  // "Anden <brand>-model" option (value=__custom_brand_<Brand>__).
  const isCustomChoice = selectedMachine === '__custom__' || selectedMachine.startsWith('__custom_brand_');
  // Group catalog machines by brand for a less technical dropdown
  const machinesByBrand = React.useMemo(() => {
    const grouped = {};
    for (const m of machines) {
      const brand = (m.brand || 'Andre').trim();
      if (!grouped[brand]) grouped[brand] = [];
      grouped[brand].push(m);
    }
    return Object.keys(grouped)
      .sort((a, b) => a.localeCompare(b, 'da'))
      .map((brand) => [brand, grouped[brand]]);
  }, [machines]);

  // Load machines lazily once when the dialog opens
  useEffect(() => {
    if (!open) return;
    // Fire onboarding_started once per open. We don't dedupe across remounts
    // because the dialog typically opens once per user lifetime — the
    // `onboarding_completed` flag prevents reopening.
    trackEvent(ANALYTICS_EVENTS.ONBOARDING_STARTED);
    let cancelled = false;
    (async () => {
      setMachinesLoading(true);
      try {
        const res = await axios.get(`${API}/machine-catalog/list`);
        if (!cancelled) setMachines(res.data.machines || []);
      } catch (err) {
        console.error('[Onboarding] failed to load machines', err);
      } finally {
        if (!cancelled) setMachinesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const toggleInterest = (key) => {
    setInterests((prev) => (prev.includes(key) ? prev.filter((i) => i !== key) : [...prev, key]));
  };

  // Prefill volume from catalog row when the user picks a catalog machine.
  // Done in the change handler (not useEffect) to avoid set-state-in-effect.
  const handleMachineChange = (name) => {
    setSelectedMachine(name);
    if (name === '__custom__') {
      // Generic custom — clear name, clear volume, default unit to ml.
      setCustomMachineName('');
      setMachineVolume('');
      setVolumeUnit('ml');
      return;
    }
    if (name.startsWith('__custom_brand_')) {
      // Brand-specific custom (e.g. "Anden Ninja-model"). Pre-fill the
      // brand prefix so the user just types their model name. Default
      // unit = ml since consumer machines are usually spec'd in ml.
      const brand = name.replace('__custom_brand_', '');
      setCustomMachineName(`${brand} `);
      setMachineVolume('');
      setVolumeUnit('ml');
      return;
    }
    if (!name) return;
    const m = machines.find((x) => x.name === name);
    if (!m) return;
    const cap = m.capacity_l || (m.tank_sizes_l && m.tank_sizes_l[0]);
    if (cap) {
      // Catalog stores capacity in litres. Switch the unit toggle to L so
      // the displayed value matches the catalog spec exactly — otherwise
      // "1.4" + unit=ml would render as 1.4 ml, which is wrong.
      setVolumeUnit('L');
      setMachineVolume(String(cap));
    }
  };

  const persist = async ({ skipped }) => {
    setSaving(true);
    try {
      // Even when the user clicks "Spring over" we still persist whatever they
      // have already filled in — otherwise their interests/machine choices are
      // silently thrown away. Only if NOTHING has been selected do we hit the
      // /skip endpoint that just flips the flag.
      const hasAnyChoice =
        selectedMachine ||
        customMachineName.trim() ||
        machineVolume.trim?.() ||
        interests.length > 0;

      if (skipped && !hasAnyChoice) {
        await axios.post(`${API}/user/onboarding/skip`);
      } else {
        // Resolve the machine fields: catalog choice or user-typed custom
        const isCustom = isCustomChoice;
        const machineName = isCustom ? customMachineName.trim() : selectedMachine || null;
        // Convert user-typed value to liters based on the selected unit so
        // we always persist a consistent canonical unit.
        const rawVolume = parseFloat((machineVolume || '').toString().replace(',', '.'));
        const volume = Number.isFinite(rawVolume) && rawVolume > 0
          ? (volumeUnit === 'ml' ? rawVolume / 1000 : rawVolume)
          : NaN;
        await axios.post(`${API}/user/onboarding`, {
          machine_name: machineName || null,
          machine_volume_l: Number.isFinite(volume) && volume > 0 ? volume : null,
          is_custom_machine: isCustom && !!machineName,
          interests,
        });
        // Grant a one-time, session-scoped welcome access to the matching
        // categories so the user can immediately preview the value of their
        // pick. Tied to the current session_token — disappears on logout /
        // new login. Does NOT affect real Pro logic.
        if (interests.length > 0) {
          grantWelcomeAccess(interests);
          trackEvent(ANALYTICS_EVENTS.WELCOME_ACCESS_GRANTED, {
            interests,
            machine_is_custom: isCustom && !!machineName,
            has_machine: !!machineName,
          });
          // Fire one interest_selected per chosen interest so GA4 can group by
          // dimension (e.g. funnel of "cocktail" interest → purchase).
          for (const interest of interests) {
            trackEvent(ANALYTICS_EVENTS.INTEREST_SELECTED, {
              interest,
              source: 'onboarding',
            });
          }
        }
        if (machineName) {
          trackEvent(ANALYTICS_EVENTS.MACHINE_SELECTED, {
            machine_name: machineName,
            machine_capacity_l: Number.isFinite(volume) && volume > 0 ? volume : null,
            is_custom_machine: isCustom,
            source: 'onboarding',
          });
        }
        trackEvent(ANALYTICS_EVENTS.ONBOARDING_COMPLETED, {
          skipped: !!skipped,
          had_interests: interests.length > 0,
          had_machine: !!machineName,
          interests_count: interests.length,
        });
      }
      // Refresh user so onboarding_completed becomes true everywhere
      await checkAuth();
    } catch (err) {
      console.error('[Onboarding] save failed', err);
      toast.error(t('onboarding.saveError', 'Kunne ikke gemme dine valg. Prøv igen.'));
      setSaving(false);
      return false;
    }
    setSaving(false);
    return true;
  };

  const handleSkip = async () => {
    // Anonymous guests have no account to persist preferences to — just close
    // the dialog so the guest-onboarding-completed flag is set and the topbar
    // Guide button can hide itself.
    if (!user) {
      closeOnboarding();
      if (interests.length > 0) navigate('/recipes');
      return;
    }
    const ok = await persist({ skipped: true });
    if (!ok) return;
    closeOnboarding();
    // If the user already picked interests, take them straight to the recipes
    // list so they see the effect right away. Otherwise stay on the homepage.
    if (interests.length > 0) navigate('/recipes');
  };

  const handleFinish = async (action) => {
    // Same short-circuit as handleSkip for anonymous guests.
    if (!user) {
      closeOnboarding();
      if (action === 'match') navigate('/match');
      if (action === 'create') navigate('/add-recipe');
      if (action === 'recipes') navigate('/recipes');
      return;
    }
    const ok = await persist({ skipped: false });
    if (!ok) return;
    closeOnboarding();
    if (action === 'match') navigate('/match');
    if (action === 'create') navigate('/add-recipe');
    if (action === 'recipes') navigate('/recipes');
  };

  if (!open) return null;

  const stepDots = [1, 2, 3, 4, 5];

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4 overflow-y-auto"
      data-testid="onboarding-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
    >
      <div className="relative w-full sm:max-w-lg bg-white rounded-2xl shadow-2xl max-h-[88vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            {stepDots.map((d) => (
              <span
                key={d}
                className={`h-2 rounded-full transition-all ${
                  d === step ? 'w-6 bg-blue-600' : d < step ? 'w-2 bg-blue-300' : 'w-2 bg-gray-200'
                }`}
              />
            ))}
            <span className="ml-2 text-xs text-gray-500">{step} / 5</span>
          </div>
          <button
            type="button"
            onClick={handleSkip}
            disabled={saving}
            data-testid="onboarding-skip"
            className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1.5 disabled:opacity-50"
          >
            <span>{t('onboarding.skip', 'Spring over')}</span>
            <FaTimes className="text-xs" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 sm:py-6">
          {step === 1 && (
            <div className="space-y-4" data-testid="onboarding-step-machine">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600 text-lg">
                  <FaCog />
                </span>
                <div>
                  <h2 id="onboarding-title" className="text-lg font-bold text-gray-900">
                    {t('onboarding.machine.title', 'Hvilken maskine bruger du?')}
                  </h2>
                  <p className="text-xs text-gray-500">
                    {t(
                      'onboarding.machine.help',
                      'Vælg dit mærke/model — eller spring listen over og skriv selv. Du kan altid ændre det senere under Profil.'
                    )}
                  </p>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700">
                  {t('onboarding.machine.label', 'Vælg maskine')}
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={selectedMachine}
                  onChange={(e) => handleMachineChange(e.target.value)}
                  disabled={machinesLoading}
                  data-testid="onboarding-machine-select"
                >
                  <option value="">
                    {machinesLoading
                      ? t('onboarding.machine.loading', 'Henter maskiner...')
                      : t('onboarding.machine.placeholder', '-- Vælg din maskine --')}
                  </option>
                  <option value="__custom__">
                    {t('onboarding.machine.custom', '📝 Anden maskine (skriv selv)')}
                  </option>
                  {machinesByBrand.map(([brand, ms]) => (
                    <optgroup key={brand} label={brand}>
                      {ms.map((m) => (
                        <option key={m.name} value={m.name}>
                          {m.name}
                        </option>
                      ))}
                      {/* Brand-specific escape hatch — the catalog can never
                          cover every Ninja/Wilfa/Camry SKU, so let the user
                          pick "Anden <brand>-model" and type both the
                          model name and tank size by hand. */}
                      <option value={`__custom_brand_${brand}`}>
                        📝 {t('onboarding.machine.customBrand', 'Anden {{brand}}-model', { brand })}
                      </option>
                    </optgroup>
                  ))}
                </select>
              </div>

              {isCustomChoice && (
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700">
                    {t('onboarding.machine.customNameLabel', 'Maskinens navn / mærke')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('onboarding.machine.customNamePlaceholder', 'Fx Ninja Slushi, Wilfa Frost...')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={customMachineName}
                    onChange={(e) => setCustomMachineName(e.target.value)}
                    data-testid="onboarding-machine-custom-name"
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700">
                  {t('onboarding.machine.volumeLabel', 'Volumen pr. tank')}
                </label>
                {/* Unit-aware input: number on the left + L/ml dropdown on the
                    right. Defaults to L because virtually every catalog
                    spec sheet uses litres, but the dropdown removes any
                    ambiguity for users who only think in millilitres. */}
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="0"
                    step={volumeUnit === 'ml' ? '10' : '0.1'}
                    inputMode="decimal"
                    placeholder={volumeUnit === 'ml' ? '1500' : '1.5'}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={machineVolume}
                    onChange={(e) => setMachineVolume(e.target.value)}
                    data-testid="onboarding-machine-volume"
                  />
                  <select
                    value={volumeUnit}
                    onChange={(e) => setVolumeUnit(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-3 text-base font-semibold bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="onboarding-machine-volume-unit"
                    aria-label="Enhed for volumen"
                  >
                    <option value="L">Liter (L)</option>
                    <option value="ml">Milliliter (ml)</option>
                  </select>
                </div>
                {/* Live conversion preview — eliminates the "wait, did I type
                    that right?" doubt that caused the 1.89-vs-1890 bug. */}
                {(() => {
                  const n = parseFloat((machineVolume || '').toString().replace(',', '.'));
                  if (!Number.isFinite(n) || n <= 0) return null;
                  const liters = volumeUnit === 'ml' ? n / 1000 : n;
                  const ml = Math.round(liters * 1000);
                  return (
                    <p className="text-xs text-blue-700 font-medium" data-testid="onboarding-volume-preview">
                      = {liters.toLocaleString('da-DK', { maximumFractionDigits: 3 })} L ({ml.toLocaleString('da-DK')} ml)
                    </p>
                  );
                })()}
                <p className="text-xs text-gray-500">
                  {t(
                    'onboarding.machine.volumeHint',
                    'Bruges til at skalere opskrifter til din maskinestørrelse.'
                  )}
                </p>
              </div>

              <p className="text-xs text-gray-500">
                {t('onboarding.machine.skipHint', 'Har du ikke en maskine endnu? Spring over – du kan altid vælge senere.')}
              </p>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="onboarding-step-interests">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-pink-100 text-pink-600 text-lg">
                  <FaHeart />
                </span>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    {t('onboarding.interests.title', 'Hvad vil du helst lave?')}
                  </h2>
                  <p className="text-xs text-gray-500">
                    {t('onboarding.interests.help', 'Vælg én eller flere. Vi viser dig opskrifter, der passer.')}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {INTEREST_OPTIONS.map((opt) => {
                  const active = interests.includes(opt.key);
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => toggleInterest(opt.key)}
                      data-testid={`onboarding-interest-${opt.key}`}
                      aria-pressed={active}
                      className={`relative flex items-center gap-2 px-4 py-4 rounded-xl border-2 text-left transition-all ${
                        active
                          ? 'border-blue-600 bg-blue-50 text-blue-900 shadow-sm'
                          : 'border-gray-200 hover:border-gray-300 text-gray-800'
                      }`}
                    >
                      <span className="text-2xl">{opt.emoji}</span>
                      <span className="text-sm font-semibold">{opt.label}</span>
                      {active && (
                        <span className="absolute top-2 right-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-white text-[10px]">
                          <FaCheck />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="onboarding-step-brix">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-600 text-lg font-bold">
                  °Bx
                </span>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    {t('onboarding.brix.title', 'Hvad er Brix?')}
                  </h2>
                  <p className="text-xs text-gray-500">
                    {t(
                      'onboarding.brix.subtitle',
                      'Den vigtigste værdi for en perfekt slush.'
                    )}
                  </p>
                </div>
              </div>

              <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
                <p>
                  {t(
                    'onboarding.brix.body1',
                    'Brix er et mål for sukkerindholdet og er afgørende for, om en slush får den rigtige konsistens.'
                  )}
                </p>
                <p>
                  {t(
                    'onboarding.brix.body2',
                    'Det er derfor, alle opskrifter i SlushBook har en Brix-værdi.'
                  )}
                </p>
                <p>
                  {t(
                    'onboarding.brix.body3',
                    'Du behøver ikke selv at beregne noget. SlushBook hjælper dig med at ramme den rigtige værdi til netop din maskine.'
                  )}
                </p>
                <p className="text-xs text-gray-500 italic">
                  {t(
                    'onboarding.brix.body4',
                    'Ønsker du at lære mere om Brix, finder du under alle opskrifter et link med en mere uddybende forklaring.'
                  )}
                </p>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4" data-testid="onboarding-step-subscriptions">
              <div className="text-center space-y-1">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-yellow-300 to-orange-400 shadow-sm">
                  <FaCrown className="text-white text-xl" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">
                  {t(
                    'onboarding.subscriptions.title',
                    'SlushBook findes i flere udgaver'
                  )}
                </h2>
                <p className="text-xs text-gray-500 max-w-md mx-auto">
                  {t(
                    'onboarding.subscriptions.subtitle',
                    'Du kommer helt gratis i gang. Herunder ser du hvad du kan opgradere til senere hvis du får lyst.'
                  )}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {SUBSCRIPTION_ORDER.map((key) => {
                  const tier = SUBSCRIPTIONS[key];
                  const isComingSoon = tier.comingSoon === true;
                  return (
                    <div
                      key={key}
                      data-testid={`onboarding-tier-${key}`}
                      className={`relative rounded-xl border-2 p-3 text-left ${
                        tier.highlight
                          ? 'border-orange-400 bg-gradient-to-b from-orange-50 to-white'
                          : 'border-gray-200 bg-white'
                      } ${isComingSoon ? 'opacity-75' : ''}`}
                    >
                      {tier.highlight && (
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                          {tier.highlightLabel || 'Mest populær'}
                        </div>
                      )}
                      {isComingSoon && (
                        <div
                          className="absolute -top-2 left-1/2 -translate-x-1/2 bg-gray-500 text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                          data-testid={`onboarding-coming-soon-${key}`}
                        >
                          {tier.comingSoonLabel || 'Kommer snart'}
                        </div>
                      )}
                      <div className="text-base font-bold text-gray-900">{tier.name}</div>
                      <div className="text-lg font-extrabold text-gray-900 mt-1">
                        {tier.price}
                      </div>
                      <div className="text-[10px] text-gray-500 mb-2">{tier.priceHint}</div>
                      <ul className="space-y-1">
                        {tier.features.slice(0, 4).map((f, i) => (
                          <li key={i} className="text-[11px] text-gray-700 flex gap-1.5">
                            <span className="text-green-500 flex-shrink-0">✓</span>
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button
                  type="button"
                  onClick={() => setStep(5)}
                  disabled={saving}
                  data-testid="onboarding-continue-free"
                  className="w-full sm:flex-1 h-11 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
                >
                  {t('onboarding.subscriptions.continueFree', 'Fortsæt gratis')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(3)}
                  disabled={saving}
                  data-testid="onboarding-subs-back"
                  className="w-full sm:w-auto h-11 border-gray-300 text-sm"
                >
                  ← {t('onboarding.back', 'Tilbage')}
                </Button>
              </div>
              <p className="text-[11px] text-gray-500 text-center pt-1">
                {t(
                  'onboarding.subscriptions.hint',
                  'Du kan altid opgradere senere via Indstillinger.'
                )}
              </p>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5" data-testid="onboarding-step-welcome">
              <div className="text-center space-y-2">
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-yellow-300 via-green-400 to-blue-500 text-3xl shadow-md">
                  <img src="/slushbook-smiley.png" alt="" className="h-12 w-12 rounded-lg" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {t('onboarding.welcome.title', 'Velkommen til SlushBook')}
                  {user?.name ? `, ${user.name.split(' ')[0]}!` : '!'}
                </h2>
                <p className="text-sm text-gray-600">
                  {t(
                    'onboarding.welcome.body',
                    'Vi har fundet gratis opskrifter, der passer til din maskine og dine interesser. Kom i gang nu:'
                  )}
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <Button
                  type="button"
                  onClick={() => handleFinish('recipes')}
                  disabled={saving}
                  data-testid="onboarding-cta-recipes"
                  className="w-full h-12 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white text-base font-semibold flex items-center justify-center gap-2"
                >
                  <span className="text-xl">⭐</span>
                  {t('onboarding.welcome.seeRecipes', 'Se anbefalede opskrifter')}
                </Button>
                <Button
                  type="button"
                  onClick={() => handleFinish('match')}
                  disabled={saving}
                  data-testid="onboarding-cta-match"
                  className="w-full h-12 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-base font-semibold flex items-center justify-center gap-2"
                >
                  <FaMagic />
                  {t('onboarding.welcome.tryMatch', 'Prøv Smart Matching')}
                </Button>
                <Button
                  type="button"
                  onClick={() => handleFinish('create')}
                  disabled={saving}
                  variant="outline"
                  data-testid="onboarding-cta-create"
                  className="w-full h-12 text-base font-semibold flex items-center justify-center gap-2 border-gray-300"
                >
                  <FaPlus />
                  {t('onboarding.welcome.createFirst', 'Opret min første opskrift')}
                </Button>
              </div>

              {/* Tilbage til subscriptions step + hint about how to re-launch later */}
              <div className="pt-2 border-t border-gray-100 space-y-2">
                <button
                  type="button"
                  onClick={() => setStep(4)}
                  data-testid="onboarding-step4-back"
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  ← {t('onboarding.back', 'Tilbage')}
                </button>
                <p className="text-xs text-gray-500 text-center" data-testid="onboarding-reopen-hint">
                  💡 {isGuestUser
                    ? t(
                        'onboarding.welcome.reopenHintGuest',
                        'Denne guide vises kun første gang du besøger SlushBook. Ønsker du senere at ændre dine præferencer eller få adgang til flere funktioner, kan du oprette en gratis bruger eller vælge et Light- eller Pro-abonnement.'
                      )
                    : t(
                        'onboarding.welcome.reopenHint',
                        'Du kan altid starte denne guide igen via Guide-ikonet i topmenuen.'
                      )}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer (steps 1, 2 + 3 only — steps 4 (subscriptions) og 5 (welcome) har deres egne CTAs) */}
        {step < 4 && (
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-gray-100">
            <Button
              type="button"
              variant="ghost"
              onClick={() => (step === 1 ? handleSkip() : setStep(step - 1))}
              disabled={saving}
              data-testid="onboarding-back"
              className="text-gray-600"
            >
              {step === 1 ? t('onboarding.skip', 'Spring over') : t('onboarding.back', 'Tilbage')}
            </Button>
            <Button
              type="button"
              onClick={() => setStep(step + 1)}
              disabled={saving}
              data-testid="onboarding-next"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6"
            >
              {step === 3
                ? t('onboarding.brix.continue', 'Forstået – fortsæt')
                : `${t('onboarding.next', 'Næste')} →`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WelcomeOnboarding;
